// EthicCred — Legal document content.
// 5 docs (Terms, Privacy, Consent, Cert Issuance, Dispute Resolution) are sourced
// from the official EthicCred legal PDFs provided by the company.
// Remaining docs use brand-aligned summary content marked clearly as PLACEHOLDER.
//
// Each entry has: slug, title, category, lastUpdated, content (markdown-lite)

const LAST = "11 February 2026";

export const LEGAL_DOCS = {
  "terms-of-service": {
    slug: "terms-of-service",
    title: "Terms of Service",
    category: "Core",
    lastUpdated: LAST,
    content: `## ETHICCRED TERMS OF SERVICE

## 1. Acceptance of Terms
By accessing, registering for, using, or interacting with EthicCred, its websites, applications, APIs, services, products, and related platforms ("Platform"), you agree to be legally bound by these Terms of Service. If you do not agree to these Terms, you must discontinue use of the Platform.

## 2. Platform Purpose
EthicCred is a professional passport, credential management, verification, certification, and professional record platform. The Platform may enable individuals, employers, educational institutions, recruiters, organizations, and authorized entities to create, manage, issue, receive, verify, store, share, and review professional records and credentials.

## 3. No Employment Guarantee
EthicCred does not guarantee employment, hiring outcomes, promotions, business opportunities, professional competence, character, ethics, trustworthiness, future conduct, income levels, or educational admissions. Users remain solely responsible for their own decisions.

## 4. User Accounts
Users must provide accurate information, maintain account security, protect passwords and credentials, and promptly notify EthicCred of unauthorized access. Users are responsible for all activities occurring under their accounts.

## 5. User Roles
Platform users may include individuals, students, interns, volunteers, employees, professionals, employers, HR representatives, educational institutions, recruiters, and administrators. EthicCred may create, modify, suspend, or remove role permissions at its discretion.

## 6. Professional Records
Professional records may include employment records, internship records, volunteer records, conduct certificates, experience certificates, recommendation letters, training records, educational records, and professional achievements. Issuing organizations remain solely responsible for records they submit.

## 7. Certificates and Recommendations
Certificates, recommendations, endorsements, evaluations, conduct assessments, and professional opinions are provided by issuing organizations. EthicCred does not independently verify every statement contained in such records and does not guarantee their accuracy.

## 8. Verification Services
Verification results reflect information available to the Platform at the time of verification. Verification should not be interpreted as a guarantee, warranty, certification of character, or prediction of future conduct.

## 9. User Consent
Users authorize EthicCred to collect, process, store, verify, display, and share information according to user permissions, platform settings, applicable laws, and platform policies.

## 10. Disputed Records
Users may challenge records they believe are inaccurate. EthicCred may request evidence, suspend visibility, add dispute notices, maintain records, remove records, or restrict accounts. EthicCred retains sole discretion regarding dispute management.

## 11. Prohibited Activities
Users may not submit false information, impersonate individuals or organizations, issue unauthorized certificates, misrepresent employment or educational qualifications, commit fraud, violate privacy rights, interfere with platform operations, attempt unauthorized access, scrape platform data, reverse engineer platform systems, or circumvent security controls.

## 12. Organization Responsibilities
Organizations must ensure that submitted records are lawful, authorized, and accurate to the best of their knowledge, and that necessary permissions have been obtained. Organizations bear full responsibility for records they issue.

## 13. Intellectual Property
All platform software, designs, logos, trademarks, databases, systems, content, interfaces, and intellectual property rights remain the exclusive property of EthicCred or its licensors.

## 14. User Content License
Users grant EthicCred a non-exclusive, worldwide, royalty-free license to store, process, display, reproduce, transmit, and use submitted content solely for platform operations, verification, security, compliance, support, and related business purposes.

## 15. Privacy
Use of the Platform is governed by the EthicCred Privacy Policy.

## 16. Security
Users acknowledge that no system can guarantee absolute security. EthicCred implements reasonable security measures but cannot guarantee uninterrupted or error-free operation.

## 17. Third-Party Services
EthicCred may integrate with third-party services. EthicCred is not responsible for the availability, accuracy, content, security, or actions of third-party services.

## 18. Fees and Subscriptions
Certain services may require payment. Fees, billing cycles, subscription terms, renewals, and refund eligibility shall be governed by applicable subscription policies.

## 19. Account Suspension
EthicCred may suspend, restrict, terminate, investigate, or remove any account or content for policy violations, fraud concerns, security risks, legal obligations, or abuse of platform services.

## 20. Audit Logs
Users acknowledge that EthicCred may maintain audit logs, access logs, verification logs, consent logs, security logs, and related records for compliance, security, fraud prevention, dispute resolution, and operational purposes.

## 21. Disclaimer of Warranties
The Platform is provided on an "AS IS" and "AS AVAILABLE" basis. EthicCred makes no warranties regarding accuracy, completeness, reliability, availability, fitness for a particular purpose, or non-infringement.

## 22. Limitation of Liability
To the maximum extent permitted by law, EthicCred, its officers, directors, employees, affiliates, contractors, and partners shall not be liable for employment decisions, hiring decisions, promotion decisions, business losses, indirect damages, consequential damages, reputational damages, lost profits, lost opportunities, or data loss.

## 23. Indemnification
Users agree to defend, indemnify, and hold harmless EthicCred from claims, damages, liabilities, losses, costs, and expenses arising from user content, user actions, policy violations, fraudulent submissions, or unauthorized activities.

## 24. International Use
Users acknowledge that the Platform may operate globally and that information may be processed, transferred, and stored across multiple jurisdictions.

## 25. Changes to Terms
EthicCred may modify these Terms at any time. Continued use of the Platform after modifications constitutes acceptance of the revised Terms.

## 26. Governing Law and Dispute Resolution
These Terms shall be governed by the laws specified by EthicCred in its legal notices and jurisdiction policies. Disputes shall first be subject to good-faith negotiation before formal proceedings.

## 27. Contact Information
For legal, privacy, compliance, or platform-related matters, users may contact EthicCred through the official contact channels published on the Platform.`,
  },

  "privacy-policy": {
    slug: "privacy-policy",
    title: "Privacy Policy",
    category: "Core",
    lastUpdated: LAST,
    content: `## ETHICCRED PRIVACY POLICY

## 1. Introduction
EthicCred ("EthicCred", "we", "our", or "us") is committed to protecting the privacy, security, and integrity of personal information entrusted to us. This Privacy Policy explains how EthicCred collects, uses, stores, processes, protects, shares, and manages information relating to users, organizations, institutions, recruiters, employers, and other authorized parties using the EthicCred platform.

## 2. Information We Collect
We may collect Account Information (name, photograph, email, mobile, username, authentication), Professional Information (professional stage, employment, internship, volunteer history, achievements, skills, certifications, recommendations, conduct certificates, experience certificates, educational records, training records), Organization Information, Verification Information (verification records, requests, results, consent records, logs), Technical Information (device, browser, OS, IP address, session, usage, security logs), and Information Voluntarily Submitted.

## 3. Sensitive Information
EthicCred may process certain sensitive information where necessary and legally permitted: government-issued identification, identity verification, financial information, professional licensing, verification-related documents. Sensitive information shall receive additional security protections and access restrictions.

## 4. Purposes of Processing
We may use information to create and manage accounts, generate Global EthicCred IDs, maintain professional passports, issue certificates and credentials, verify records, process verification requests, authenticate users, detect fraud, prevent abuse, improve platform functionality, provide customer support, generate audit records, enforce platform policies, comply with legal obligations, and protect users and platform security.

## 5. Professional Passport Records
EthicCred maintains professional records including employment history, internship history, volunteer records, experience certificates, conduct certificates, recommendation letters, educational credentials, and professional achievements. EthicCred does not independently guarantee the accuracy, completeness, legality, or reliability of records submitted by third parties.

## 6. Information Displayed Through Verification
We may display verification-safe information such as name, photograph, professional stage, employment history, certificates, recommendations, verification status, and achievements. Specific information displayed varies based on permissions, consent, verification settings, applicable laws, and platform policies.

## 7. Confidential and Restricted Information
Unless expressly authorized by the user, required by law, or necessary for platform operations, EthicCred will not publicly display, disclose, or make generally available confidential, personal, financial, identity-related, authentication-related, contact-related, or other sensitive information that could be used to identify, impersonate, locate, access, or compromise an individual.

## 8. Consent
Users authorize EthicCred to collect, store, process, display, verify, and manage information necessary to provide platform services. Users may withdraw certain permissions where legally permitted, subject to legitimate business, legal, audit, fraud-prevention, and compliance requirements.

## 9. Information Sharing
EthicCred may share information with authorized employers, educational institutions, recruiters, approved verification requestors, service providers, payment processors, security providers, and legal authorities where required. EthicCred does not sell personal information to third parties.

## 10. International Data Transfers
EthicCred operates globally. Information may be processed, stored, transmitted, or accessed across multiple jurisdictions where EthicCred, its affiliates, infrastructure providers, contractors, or service providers operate.

## 11. Data Retention
EthicCred may retain professional records, certificates, verification logs, consent records, audit logs, security records, and account records for as long as reasonably necessary for platform operations, legal obligations, dispute resolution, fraud prevention, security, compliance, and legitimate business purposes.

## 12. User Rights
Subject to applicable laws, users may have rights to access, correct, update, request deletion, request restrictions, withdraw consent, export information, and object to certain processing. EthicCred may require identity verification before processing requests.

## 13. Security
EthicCred implements administrative, technical, organizational, and security measures designed to protect information. However, no internet-based platform can guarantee absolute security.

## 14. Fraud Prevention and Abuse Detection
EthicCred may collect, monitor, analyze, and retain information necessary to detect fraud, prevent impersonation, prevent unauthorized certificate issuance, detect abuse, investigate suspicious activity, and protect platform integrity.

## 15. Cookies and Tracking
EthicCred may use cookies, analytics tools, tracking technologies, and similar technologies to improve functionality, security, user experience, and platform performance.

## 16. Children and Minors
Where permitted by law, minors may access certain services through authorized educational institutions, guardians, or lawful consent mechanisms. Additional protections may apply to minors.

## 17. Third-Party Services
The Platform may integrate with third-party services. EthicCred is not responsible for third-party privacy practices, policies, content, or operations.

## 18. Changes to This Policy
EthicCred may update this Privacy Policy periodically. Updated versions will become effective upon publication unless otherwise specified.

## 19. Contact Information
Questions regarding privacy, data protection, security, compliance, or information management may be directed to EthicCred through the official contact information published on the Platform.`,
  },

  "consent-verification-policy": {
    slug: "consent-verification-policy",
    title: "Consent & Verification Policy",
    category: "Core",
    lastUpdated: LAST,
    content: `## ETHICCRED CONSENT & VERIFICATION POLICY

## 1. Purpose
This Consent & Verification Policy governs how professional records, certificates, employment records, recommendations, educational records, internship records, volunteer records, verification requests, and related information may be collected, submitted, verified, viewed, disclosed, and managed through the EthicCred Platform.

## 2. Professional Passport System
EthicCred operates as a Professional Passport Platform. Users may maintain a Professional Passport containing professional records, credentials, certificates, recommendations, employment records, educational records, internship records, volunteer records, and related information.

## 3. User Consent
By creating an account, accepting platform terms, receiving records through the Platform, submitting records to the Platform, or using verification services, users authorize EthicCred to store records, process records, verify records, display authorized records, maintain audit logs, maintain verification history, maintain consent records, and share information according to platform permissions and applicable laws.

## 4. Record Ownership
Users retain ownership of information and records they are legally entitled to own. Organizations retain ownership of records they create and issue. EthicCred retains ownership of the platform, software, systems, databases, verification processes, audit systems, and related intellectual property.

## 5. Certificate Issuance
Authorized organizations may issue Experience Certificates, Conduct Certificates, Recommendation Letters, Internship Certificates, Volunteer Certificates, Educational Certificates, Training Certificates, and Achievement Certificates. Issuing organizations are solely responsible for the content, accuracy, legality, and authorization of records they issue.

## 6. Verification Requests
Verification requests may be initiated by the individual user, employers, educational institutions, recruiters, authorized organizations, or other authorized parties approved by EthicCred. All verification requests may be logged for security, compliance, fraud prevention, audit, and dispute management purposes.

## 7. Verification Results
Verification results represent information available to EthicCred at the time of verification. Verification does not constitute an employment recommendation, hiring recommendation, character certification, ethics certification, future conduct prediction, professional competency guarantee, or legal clearance.

## 8. Conduct Certificates
Conduct Certificates represent professional assessments issued by authorized organizations. They do not represent an independent assessment by EthicCred. EthicCred does not certify honesty, integrity, ethics, character, trustworthiness, reliability, or future conduct.

## 9. Recommendations
Recommendation Letters, endorsements, and recommendations are opinions of the issuing organization or authorized representative. Such recommendations do not represent the views of EthicCred.

## 10. Professional Record Display
EthicCred may display selected professional information through public profiles, verification pages, verification reports, QR verification pages, and shared profile links. Displayed information shall be limited to information authorized by platform settings, user permissions, organizational permissions, applicable laws, and platform policies.

## 11. Restricted Information
EthicCred shall not intentionally disclose confidential or restricted information through public verification services unless authorized by the user or the issuing organization, required by law, or necessary for platform operations.

## 12. Consent Records
EthicCred may maintain records of user consent, verification consent, data sharing consent, certificate acceptance, verification requests, and access history. Such records may be used to demonstrate compliance, resolve disputes, prevent fraud, and protect platform integrity.

## 13. Audit Logs
EthicCred may record verification requests, profile access, certificate issuance, certificate updates, certificate revocations, record modifications, user actions, and administrative actions. Audit logs may be retained for compliance, security, legal, and operational purposes.

## 14. Disputed Records
Users may dispute records they believe are inaccurate, incomplete, misleading, unauthorized, or unlawful. EthicCred may request supporting evidence, contact issuing organizations, add dispute notices, restrict visibility, suspend records, remove records, or maintain records where evidence supports retention.

## 15. Fraudulent or False Submissions
Organizations and users are prohibited from submitting false, misleading, fraudulent, fabricated, manipulated, unauthorized, or defamatory records. EthicCred may suspend accounts, restrict platform access, remove records, revoke certificates, reject future submissions, notify affected parties, or cooperate with lawful investigations.

## 16. Certificate Revocation
Issuing organizations may request revocation of certificates where administrative errors occurred, fraud is discovered, unauthorized issuance occurred, or corrections are required. EthicCred may maintain revocation records and historical audit records.

## 17. No Guarantee of Accuracy
EthicCred relies on information submitted by users and organizations. EthicCred does not guarantee the accuracy, completeness, reliability, legality, or validity of information submitted by third parties.

## 18. Investigations
EthicCred may investigate suspected abuse, fraud, impersonation, unauthorized access, false submissions, security incidents, or policy violations. Users and organizations agree to reasonably cooperate with such investigations.

## 19. International Use
Verification standards, legal requirements, employment practices, and professional records may vary across jurisdictions. EthicCred does not guarantee that records satisfy the requirements of any particular employer, institution, regulator, licensing authority, or jurisdiction.

## 20. Policy Changes
EthicCred may modify this Policy from time to time. Continued use of the Platform constitutes acceptance of any updated version.

## 21. Contact
Questions regarding verification, consent, disputes, record management, or certificate issuance may be directed to EthicCred through the official contact channels published on the Platform.`,
  },

  "certificate-issuance-policy": {
    slug: "certificate-issuance-policy",
    title: "Certificate Issuance Policy",
    category: "Issuers",
    lastUpdated: LAST,
    content: `## ETHICCRED CERTIFICATE ISSUANCE POLICY

## 1. Purpose
This Certificate Issuance Policy establishes the rules, responsibilities, standards, procedures, and requirements governing the creation, issuance, acceptance, storage, verification, modification, revocation, and management of certificates and professional records issued through the EthicCred Platform.

## 2. Objective
This Policy is intended to promote professional credibility and transparency, enable issuance of verifiable professional records, maintain trust in the EthicCred ecosystem, prevent misuse, fraud, misrepresentation, and unauthorized certificate issuance, and protect individuals, organizations, and platform integrity.

## 3. Eligible Issuers
Certificates may only be issued by authorized organizations and authorized representatives, including employers, educational institutions, universities, schools, training organizations, internship providers, volunteer organizations, professional associations, and other approved organizations. EthicCred may require verification, documentation, approvals, or additional authentication before granting certificate issuance privileges.

## 4. Authorized Representatives
Organizations are responsible for ensuring that only authorized personnel issue certificates — Company Administrators, HR Managers, HR Executives, Department Heads, Reporting Managers, Educational Administrators, Training Coordinators, and Authorized Signatories. Organizations remain fully responsible for certificates issued by their representatives.

## 5. Certificate Types
EthicCred supports Experience Certificates, Conduct Certificates, Recommendation Letters, Internship Certificates, Volunteer Certificates, Apprenticeship Certificates, Educational Certificates, Training Certificates, Achievement Certificates, Recognition Certificates, Skill Verification Certificates, Professional Milestone Certificates, and other approved categories.

## 6. Certificate Content Requirements
Certificates should contain information that is accurate, relevant, authorized, professional, lawful, and non-defamatory. Certificates should not contain false statements, unverified allegations, personal attacks, offensive content, discriminatory content, confidential information not authorized for disclosure, or information prohibited by law.

## 7. Issuer Responsibility
The issuing organization is solely responsible for accuracy, completeness, authorization, legality, supporting documentation, and compliance with applicable laws. EthicCred does not independently validate every statement contained within certificates.

## 8. Professional Conduct Certificates
Conduct Certificates represent professional assessments made by the issuing organization based on workplace observations. They should not contain criminal accusations, defamatory allegations, unverified misconduct claims, medical information, political opinions, religious opinions, or personal lifestyle judgments. EthicCred does not certify character, ethics, integrity, honesty, trustworthiness, or future conduct.

## 9. Recommendation Letters
Recommendation Letters represent the opinion of the issuer and do not constitute guarantees of future performance, future conduct, employment suitability, or professional competence.

## 10. Certificate Acceptance
By accepting certificates through the Platform, recipients acknowledge that certificates are issued by third parties, EthicCred is not the issuer, EthicCred does not guarantee all statements contained within certificates, and verification does not constitute endorsement.

## 11. Certificate Verification
EthicCred may provide certificate verification services that confirm certificate existence, issuing organization, issue date, certificate identifier, verification status, and revocation status. Verification does not constitute endorsement of all certificate contents.

## 12. Unique Certificate Identifiers
Each certificate may be assigned a Certificate ID, Verification ID, QR Code, and Verification URL to enable authenticity checks and prevent forgery.

## 13. Certificate Modification
Issued certificates should generally remain unchanged. Where corrections are required, new versions may be issued, amendments may be recorded, and historical records may be retained. EthicCred may preserve audit history for compliance and security purposes.

## 14. Certificate Revocation
Certificates may be revoked where issued in error, fraud is discovered, unauthorized issuance occurred, supporting records are proven false, or legal requirements require removal. Revocation records may be maintained for audit and verification purposes.

## 15. Audit Trail
EthicCred may maintain records including issuer identity, organization identity, issue date, update history, verification activity, revocation history, and access history. Audit trails may be retained for compliance, security, dispute resolution, and fraud prevention.

## 16. Disputed Certificates
Individuals may dispute certificates believed to be inaccurate, unauthorized, fraudulent, misleading, or incomplete. EthicCred may request evidence, review supporting records, contact issuers, restrict visibility, suspend certificates, remove certificates, or add dispute notices.

## 17. Prohibited Certificates
Fraudulent, purchased, forged, fabricated, or defamatory certificates are strictly prohibited.

## 18. Fraud Investigation
EthicCred may investigate suspected certificate fraud, abuse, impersonation, unauthorized issuance, or policy violations, and may suspend accounts, restrict access, remove records, or cooperate with lawful investigations.

## 19. Limitation of Liability
EthicCred acts as a platform facilitating issuance, storage, management, and verification of certificates. EthicCred is not responsible for hiring decisions, employment outcomes, business decisions, decisions made based on certificates, or third-party statements contained in certificates.

## 20. International Application
Certificate standards vary across jurisdictions. EthicCred does not guarantee that any certificate satisfies legal, regulatory, employment, licensing, immigration, educational, or professional requirements in any particular jurisdiction.

## 21. Policy Changes
EthicCred may update this Policy periodically. Continued use of the Platform constitutes acceptance.

## 22. Contact
Questions regarding certificate issuance, verification, disputes, revocations, or compliance may be directed to EthicCred through official channels.`,
  },

  "dispute-resolution-policy": {
    slug: "dispute-resolution-policy",
    title: "Dispute Resolution Policy",
    category: "Trust & Safety",
    lastUpdated: LAST,
    content: `## ETHICCRED DISPUTE RESOLUTION POLICY

## 1. Purpose
This Dispute Resolution Policy establishes the procedures for reporting, reviewing, investigating, resolving, correcting, restricting, suspending, or removing disputed records, certificates, recommendations, verification records, professional information, and other content maintained through the EthicCred Platform.

## 2. EthicCred's Role
EthicCred acts as a technology platform facilitating professional records, certifications, recommendations, verification services, and credential management. EthicCred is not an employer, a court of law, an arbitrator, a government authority, a law enforcement agency, or a professional licensing authority. EthicCred does not determine guilt, innocence, liability, criminal responsibility, or legal rights.

## 3. Eligible Disputes
Users may submit disputes relating to employment records, internship records, volunteer records, conduct certificates, experience certificates, recommendation letters, educational records, verification results, profile information, unauthorized records, duplicate records, or identity-related concerns.

## 4. Grounds for Dispute
A dispute may be submitted where a user reasonably believes information is incorrect, incomplete, misleading, outdated, fraudulent, unauthorized, duplicated, submitted by mistake, or attributed to the wrong individual.

## 5. Dispute Submission
A dispute request should include a description of the concern, the record being disputed, supporting evidence, contact information, and any relevant explanations. Submitting false or malicious disputes is prohibited.

## 6. Initial Review
EthicCred may conduct an initial review to determine whether sufficient information has been provided, the dispute falls within platform scope, or additional evidence is required. Incomplete requests may be rejected or returned for clarification.

## 7. Investigation Process
Where appropriate, EthicCred may review submitted evidence, audit logs, and system records, contact the issuing organization, and request additional information or supporting documents. EthicCred may use reasonable efforts to review available information but is not obligated to conduct formal investigations.

## 8. Issuer Response
Where a dispute involves an organization-issued record, EthicCred may provide the issuing organization an opportunity to respond. Organizations may submit explanations, corrections, supporting evidence, requests for modification, or requests for revocation.

## 9. Temporary Restrictions
During review, EthicCred may add dispute notices, restrict visibility, temporarily suspend records, limit verification access, or prevent public display. Such actions do not imply wrongdoing by any party.

## 10. Possible Outcomes
Following review, EthicCred may maintain the record, correct the record, update the record, restrict visibility, remove the record, revoke a certificate, add explanatory notes, or close the dispute without action. EthicCred retains discretion regarding platform actions.

## 11. Professional Opinions
Conduct certificates, recommendations, endorsements, evaluations, and professional assessments may contain subjective opinions. EthicCred may decline to remove records solely because an individual disagrees with an opinion, provided the record was properly authorized and does not violate platform policies.

## 12. Fraudulent Records
Where evidence suggests fraud, forgery, impersonation, fabricated credentials, unauthorized issuance, or other serious misconduct, EthicCred may suspend records, revoke certificates, restrict accounts, remove content, preserve evidence, and conduct additional review.

## 13. False Disputes
Users are prohibited from submitting knowingly false, fraudulent, abusive, or misleading dispute claims. Repeated abuse of the dispute process may result in warnings, restrictions, suspension, or account termination.

## 14. Audit Logs
EthicCred may maintain audit records relating to disputes, evidence submissions, communications, review actions, and administrative decisions. Such records may be retained for security, compliance, legal, and operational purposes.

## 15. Appeals
Where permitted, users may request reconsideration by providing substantial new information or evidence not previously reviewed. EthicCred is not obligated to reopen disputes absent new evidence.

## 16. Serious Professional Incidents
EthicCred may establish separate policies and procedures governing allegations relating to fraud, theft, harassment, violence, data misuse, financial misconduct, or regulatory violations. Additional evidence, review standards, and safeguards may apply.

## 17. No Legal Determination
EthicCred decisions are platform administration decisions only. Platform actions do not constitute legal findings, judicial decisions, employment rulings, criminal determinations, or regulatory determinations.

## 18. Limitation of Liability
EthicCred shall not be responsible for losses arising from disputed records, user submissions, organization submissions, verification outcomes, hiring decisions, employment decisions, or business decisions. Users and organizations remain responsible for independent due diligence.

## 19. Cooperation with Legal Authorities
Where required by law, court order, regulatory requirement, or lawful government request, EthicCred may cooperate with authorities and disclose relevant information as legally required.

## 20. Policy Changes
EthicCred may update this Policy periodically. Continued use of the Platform constitutes acceptance.

## 21. Contact
Questions regarding disputes, appeals, investigations, corrections, revocations, or record management may be submitted through EthicCred's official channels.`,
  },

  // ---------- PLACEHOLDER docs (clearly marked) ----------
  "cookie-policy": {
    slug: "cookie-policy",
    title: "Cookie Policy",
    category: "Privacy",
    lastUpdated: LAST,
    content: `## ETHICCRED COOKIE POLICY

## 1. Purpose
This Cookie Policy explains how EthicCred uses cookies, similar technologies, local storage, web beacons, pixels, and related technologies on our websites, applications, platforms, APIs, and services. This Policy should be read together with the EthicCred Privacy Policy and Terms of Service.

## 2. What Are Cookies?
Cookies are small text files stored on a user's device when visiting a website or using certain online services. Cookies help websites and applications function properly, improve user experience, enhance security, remember preferences, and generate usage analytics.

## 3. Technologies We May Use
EthicCred may use Cookies, Session Cookies, Persistent Cookies, Browser Storage, Local Storage, Web Beacons, Pixels, Device Identifiers, Authentication Tokens, and similar technologies.

## 4. Types of Cookies We Use
**A. Essential Cookies** — necessary for the Platform to function: login authentication, session management, security protection, fraud prevention, account access. Without them certain services may not operate.

**B. Security Cookies** — detect suspicious activity, prevent unauthorized access, protect user accounts, prevent fraud, monitor security events.

**C. Functional Cookies** — remember preferences, language, display settings, improve user experience.

**D. Performance and Analytics Cookies** — understand platform usage, measure performance, identify technical issues, improve services. Information may be aggregated and anonymised where reasonably possible.

**E. Service Improvement Cookies** — improve platform features, enhance reliability, evaluate functionality.

## 5. Purpose of Cookies
EthicCred may use cookies to authenticate users, maintain login sessions, protect accounts, improve performance, prevent fraud, detect abuse, support verification services, maintain platform integrity, remember settings, analyse usage, and improve functionality.

## 6. Third-Party Services
The Platform may use third-party services that utilise cookies — analytics providers, authentication providers, security providers, payment processors, customer support providers, cloud service providers. Third parties are governed by their own privacy and cookie practices.

## 7. Cookies We Do NOT Use for Professional Decisions
Cookies are not used by EthicCred to make employment decisions, hiring decisions, conduct evaluations, professional rankings, or certification decisions.

## 8. Cookie Management
Most browsers allow you to view, delete, block, restrict, and configure cookies. You may modify browser settings at any time.

## 9. Impact of Disabling Cookies
Disabling certain cookies may limit functionality, prevent login, affect account security, reduce performance, or restrict access. Essential cookies are required for platform operation.

## 10. Mobile Applications
Mobile applications may use device identifiers, authentication tokens, local storage, and application preferences for security, functionality, and improvement.

## 11. Security and Fraud Prevention
We may use cookies to detect fraud, prevent account abuse, identify suspicious behaviour, and protect platform integrity.

## 12. Data Collected Through Cookies
May include IP address, browser type, device type, operating system, session information, usage patterns, login status, and security events — processed per the Privacy Policy.

## 13. Retention of Cookie Data
Retained for varying periods depending on cookie type, security requirements, operational requirements, legal obligations, and fraud-prevention requirements.

## 14. International Use
Cookie-related information may be processed and stored in multiple jurisdictions where EthicCred or its service providers operate.

## 15. Changes to This Policy
EthicCred may modify this Cookie Policy from time to time. Updated versions become effective upon publication unless otherwise stated.

## 16. Contact
Questions regarding this Cookie Policy may be directed to EthicCred through the official contact channels published on the Platform.`,
  },

  "acceptable-use-policy": {
    slug: "acceptable-use-policy",
    title: "Acceptable Use Policy",
    category: "Trust & Safety",
    lastUpdated: LAST,
    content: `## ETHICCRED ACCEPTABLE USE POLICY

## 1. Purpose
This Policy establishes the rules governing the lawful, ethical, responsible, and permitted use of the EthicCred Platform — including websites, applications, APIs, verification services, certificates, professional records, databases, and communications. It forms part of the Terms of Service and applies to all users, organisations, institutions, recruiters, employers, employees, students, contractors, and authorised representatives.

## 2. Objective
EthicCred facilitates professional identity management, credential verification, professional record management, career passport services, certifications, recommendations, and related activities. Users must use the Platform in a lawful, honest, responsible, and professional manner.

## 3. Compliance With Laws
Users are responsible for compliance with applicable employment, privacy, data protection, intellectual property, anti-discrimination, anti-harassment, consumer protection, and regulatory requirements.

## 4. Account Responsibility
Users must maintain accurate account information, protect credentials, prevent unauthorised access, use accounts only for authorised purposes, and report suspected unauthorised activity promptly. Users remain responsible for all activity under their accounts.

## 5. Authorised Uses
Managing professional records, issuing authorised certificates, verifying credentials, managing employment / education / internship / volunteer records, sharing achievements, managing recommendations, and conducting legitimate verification activity.

## 6. Prohibited Activities
Users shall not submit false information, create fake profiles, impersonate any person or organisation, misrepresent employment history, qualifications, or credentials, submit forged or altered documents, use stolen information, circumvent platform controls, interfere with operations, violate privacy, engage in fraud, or facilitate illegal activities.

## 7. Certificate Abuse
Users shall not create fake or fraudulent certificates, purchase or sell certificates without authorisation, alter contents, manipulate verification records, or misrepresent certificate status. EthicCred may suspend, revoke, or remove certificates suspected of fraud or abuse.

## 8. Professional Conduct
Harassment, threats, abuse, hate speech, defamation, discrimination, bullying, intimidation, and offensive communications are prohibited.

## 9. Restricted Content
Users may not upload content that is unlawful, fraudulent, defamatory, misleading, violates privacy or IP rights, contains malware, or contains unauthorised personal information.

## 10. Scraping and Data Extraction
Users may not scrape Platform data, harvest user information, use automated collection tools, perform mass downloads, build competing databases, or copy verification records without authorisation.

## 11. Security Violations
Users may not attempt unauthorised access, bypass authentication, probe vulnerabilities, interfere with security controls, distribute malware, conduct DoS attacks, or attempt privilege escalation.

## 12. API Usage
API users must follow documentation, respect rate limits, use authorised credentials, protect API keys, and comply with all platform policies. EthicCred may suspend API access for misuse.

## 13. Intellectual Property
Users may not copy platform software, reverse engineer systems, reproduce proprietary databases, remove ownership notices, or infringe IP rights.

## 14. Organisation Responsibilities
Organisations issuing records must ensure authorisation, maintain supporting evidence, issue records in good faith, use qualified representatives, and correct known inaccuracies. Organisations remain responsible for what they issue.

## 15. Privacy and Confidentiality
Users shall not access information without authorisation, share confidential information improperly, disclose sensitive records without permission, or attempt to obtain restricted information.

## 16. Fraud Prevention
EthicCred may monitor activity to detect identity fraud, certificate fraud, account abuse, impersonation, unauthorised access, or record manipulation. Users agree to cooperate with legitimate investigations.

## 17. Enforcement
Possible actions include warnings, content removal, certificate suspension/revocation, access restrictions, account suspension/termination, legal action, and reporting to authorities.

## 18. No Circumvention
Users may not attempt to bypass verification requirements, security controls, access restrictions, account limitations, or platform safeguards.

## 19. Reporting Violations
Users may report suspected abuse, fraud, impersonation, security concerns, or policy violations through official reporting channels.

## 20. Platform Integrity
Users shall not engage in activities that may harm platform trust, verification reliability, certificate integrity, user safety, system security, or platform reputation.

## 21. Limitation of Platform Responsibility
EthicCred is a technology platform and does not guarantee user or organisation honesty, record accuracy, or hiring/business outcomes. Users remain responsible for their own decisions.

## 22. Changes to This Policy
EthicCred may update this Policy periodically. Continued use constitutes acceptance.

## 23. Contact
Questions may be directed to EthicCred through official support, compliance, or legal contact channels.`,
  },

  "community-guidelines": {
    slug: "community-guidelines",
    title: "Community Guidelines",
    category: "Trust & Safety",
    lastUpdated: LAST,
    placeholder: true,
    content: `## ETHICCRED COMMUNITY GUIDELINES

## 1. Be Truthful
Submit only records you can support with evidence. Misrepresenting employment, education, or credentials damages trust for everyone.

## 2. Be Respectful
Recommendations, conduct certificates and reviews should be professional, fact-based, and constructive. Personal attacks, defamation, discrimination, and harassment are not permitted.

## 3. Respect Privacy
Do not share other users' confidential information without consent. Do not submit certificates that include another person's sensitive personal data.

## 4. Protect Your Account
Use strong, unique passwords. Enable multi-factor authentication when available. Never share authentication credentials. Report suspicious access immediately.

## 5. Issue Responsibly
If you are an organisation, only issue records you are authorised to issue and that you can substantiate. Treat the EthicCred badge with the gravity it deserves.

## 6. Report Issues
Help us keep EthicCred trustworthy. Use the dispute system or contact Trust & Safety if you see suspicious certificates, impersonation, or abuse.

## 7. Consequences
Repeated violations may lead to certificate revocation, account suspension, and removal from the EthicCred network.`,
  },

  "intellectual-property-policy": {
    slug: "intellectual-property-policy",
    title: "Copyright & Intellectual Property Policy",
    category: "Trust & Safety",
    lastUpdated: LAST,
    content: `## ETHICCRED COPYRIGHT & INTELLECTUAL PROPERTY POLICY

## 1. Purpose
This Policy establishes the ownership, protection, permitted use, reporting procedures, and enforcement mechanisms relating to intellectual property rights associated with the EthicCred Platform. It forms part of the Terms of Service and applies to all users, organisations, institutions, employers, recruiters, developers, API users, partners, and visitors.

## 2. Ownership of the Platform
Unless otherwise stated, EthicCred and its licensors own all rights, title, and interest in EthicCred software, source code, databases, platform architecture, user interfaces, website content, mobile applications, verification systems, APIs, designs, graphics, logos, trademarks, service marks, branding, documentation, business processes, and proprietary methodologies. All rights not expressly granted are reserved.

## 3. EthicCred Trademarks
The following may constitute trademarks, service marks, trade names, brand assets, or proprietary identifiers of EthicCred: EthicCred, EthicCred Professional Passport, EthicCred ID, Global EthicCred ID, EthicCred Verification, EthicCred Trusted Employer, EthicCred Verified Professional, EthicCred logos, and EthicCred branding materials. Users may not use EthicCred trademarks without prior written authorisation.

## 4. User Content
Users may submit professional records, employment records, educational records, certificates, recommendations, documents, images, profile information, and other content. Users retain ownership of content they lawfully own or control.

## 5. License Granted to EthicCred
By submitting content, users grant EthicCred a non-exclusive, worldwide, royalty-free, transferable, sublicensable license to store, process, display, verify, reproduce, distribute, archive, secure, analyse, and use submitted content solely for operation, improvement, protection, verification, compliance, security, and administration of the Platform.

## 6. User Warranties
Users represent and warrant that they own the content submitted, or possess sufficient rights and permissions to submit the content. Users are responsible for obtaining necessary permissions before uploading or sharing content.

## 7. Organisation-Issued Records
Organisations retain ownership of certificates, recommendations, evaluations, and records they create. Organisations grant EthicCred permission to store, verify, display, archive, and manage such records for platform operations.

## 8. Prohibited Intellectual Property Activities
Users may not copy platform software, reverse engineer systems, reproduce databases, remove ownership notices, use EthicCred branding without authorisation, misrepresent ownership, create counterfeit certificates or verification records, or infringe third-party rights.

## 9. Certificate Authenticity
Certificates may contain Certificate IDs, QR Codes, Verification URLs, and digital verification mechanisms. Users may not alter certificates, modify verification records, forge identifiers, or manipulate verification status.

## 10. Copyright Infringement Reporting
A person believing content infringes IP rights may submit a written notice containing identification of the protected work, identification of the allegedly infringing material, description of the infringement, contact information, good-faith statement, and authority statement. EthicCred may investigate and take appropriate action.

## 11. Response to Infringement Claims
Upon receiving a valid complaint, EthicCred may request additional information, restrict content, remove content, suspend access, investigate the matter, or notify affected users. EthicCred retains discretion regarding enforcement actions.

## 12. Repeat Infringers
Users repeatedly violating IP rights may be subject to warnings, content removal, account restrictions, suspension, or termination.

## 13. Third-Party Content
EthicCred may contain links, integrations, references, or content originating from third parties. Ownership of third-party content remains with the applicable owners.

## 14. Feedback and Suggestions
Unless otherwise agreed in writing, EthicCred may use voluntarily provided feedback, suggestions, feature requests, ideas, and improvements without restriction, compensation, or attribution.

## 15. Open-Source Software
EthicCred may incorporate third-party open-source software, governed by its respective licenses.

## 16. Professional Records and Verification Data
Professional records, verification data, audit records, verification identifiers, and verification histories may constitute protected databases and proprietary platform assets. Unauthorised mass extraction, copying, scraping, or republication is prohibited.

## 17. API and Data Access
Users accessing APIs, integrations, exports, or verification services may only use information for authorised purposes. Unauthorised resale, redistribution, commercial exploitation, or bulk extraction is prohibited unless expressly authorised.

## 18. Reservation of Rights
Nothing in this Policy transfers ownership of any intellectual property belonging to EthicCred or third parties. All rights remain reserved.

## 19. Limitation of Liability
EthicCred does not guarantee that user-submitted content is free from third-party IP claims. Users remain responsible for content they submit.

## 20. Policy Changes
EthicCred may update this Policy periodically. Continued use constitutes acceptance.

## 21. Contact
Questions, notices, complaints, IP concerns, trademark inquiries, licensing requests, or copyright claims may be directed to EthicCred through the official legal and compliance contact channels.`,
  },

  "subscription-refund-policy": {
    slug: "subscription-refund-policy",
    title: "Subscription & Refund Policy",
    category: "Billing",
    lastUpdated: LAST,
    content: `## ETHICCRED SUBSCRIPTION & REFUND POLICY

## 1. Purpose
This Policy governs the purchase, renewal, billing, cancellation, upgrade, downgrade, refund, and payment terms relating to EthicCred subscriptions, premium services, API services, verification services, and other paid offerings.

## 2. Subscription Services
EthicCred may offer paid services including Individual Premium Plans, Professional Plans, Employer Plans, Educational Institution Plans, Recruiter Plans, API Access Plans, Verification Services, Enterprise Plans, Custom Solutions, and Add-on Services. EthicCred reserves the right to modify, introduce, discontinue, or replace plans at any time.

## 3. Billing Cycle
Subscriptions may be offered on monthly, quarterly, semi-annual, annual, multi-year, usage-based, or enterprise-contract billing. Billing periods are specified during purchase.

## 4. Payment Methods
Accepted methods may include credit cards, debit cards, UPI, net banking, bank transfer, digital wallets, and other approved international methods. Users authorise EthicCred and its payment providers to process payments as required.

## 5. Automatic Renewal
Unless otherwise specified, subscriptions may auto-renew at the end of the period. Users may disable auto-renewal through account settings before the next renewal date.

## 6. Plan Upgrades
Users may upgrade during an active subscription period. Additional charges may apply, pro-rated billing adjustments may apply, and new features may become immediately available.

## 7. Plan Downgrades
Downgrades may reduce available features, storage limits, user limits, verification limits, and may take effect immediately or at the next billing cycle.

## 8. Cancellation
Subscribers may cancel at any time. Cancellation stops future recurring billing. Unless otherwise required by law or explicitly stated, cancellation does not automatically entitle the subscriber to a refund for amounts already paid.

## 9. Refund Policy
EthicCred generally operates a no-refund policy for used subscription periods, consumed services, completed verifications, consumed API usage, delivered digital products, and activated premium features. Refunds may be granted only where required by applicable law or where EthicCred determines exceptional circumstances exist.

## 10. Eligible Refund Scenarios
Duplicate payments, accidental duplicate subscriptions, billing system errors, unauthorised charges verified by investigation, and services not delivered due to platform error. Approval remains at EthicCred's discretion except where law requires otherwise.

## 11. Non-Refundable Services
Verification requests, certificate issuance, certificate generation, API usage charges, setup fees, implementation fees, consultation fees, data migration, custom development, and enterprise onboarding.

## 12. Free Trials
Where offered, subject to eligibility requirements, time limitations, usage restrictions, and feature limitations. EthicCred reserves the right to modify or discontinue free trials.

## 13. Enterprise Contracts
Enterprise customers may be subject to separate agreements, service orders, or negotiated contracts. Where a separate written agreement exists, it prevails over this Policy to the extent of any conflict.

## 14. Pricing Changes
EthicCred reserves the right to modify pricing, fees, billing structures, plans, and usage charges. Changes shall not affect already-paid subscription periods unless otherwise permitted by law or contract.

## 15. Taxes
Subscribers are responsible for applicable GST, VAT, sales tax, withholding tax, service tax, government fees, and regulatory charges.

## 16. Payment Failures
If payment fails, services may be restricted, features suspended, accounts downgraded, or access limited. EthicCred may attempt to collect outstanding amounts through authorised payment methods.

## 17. Account Termination
Termination for policy violations, fraud, abuse, or unlawful activity does not entitle the user to any refund.

## 18. Chargebacks and Payment Disputes
Users should contact EthicCred before initiating chargebacks. Fraudulent or abusive chargebacks may result in account suspension, service termination, recovery actions, or restriction of future purchases.

## 19. Currency
Prices may be displayed in different currencies depending on region, country, payment method, or platform settings. Exchange rates used by payment providers may vary.

## 20. Limitation of Liability
EthicCred shall not be liable for banking delays, payment processor failures, exchange rate fluctuations, third-party payment errors, or tax obligations of users.

## 21. Policy Changes
EthicCred may update this Policy periodically. Continued use of paid services constitutes acceptance.

## 22. Contact
Questions regarding billing, subscriptions, cancellations, payments, refunds, invoices, or account charges may be directed to EthicCred through official billing, finance, or support channels.`,
  },

  "data-retention-policy": {
    slug: "data-retention-policy",
    title: "Data Retention Policy",
    category: "Privacy",
    lastUpdated: LAST,
    content: `## ETHICCRED DATA RETENTION POLICY

## 1. Purpose
This Policy describes how EthicCred collects, stores, retains, archives, restricts, deletes, and manages information maintained through the Platform. It forms part of the Terms of Service, Privacy Policy, Consent & Verification Policy, and related policies.

## 2. Objectives
Retention supports maintaining professional passports, preserving verification integrity, preventing fraud, supporting dispute resolution, maintaining audit trails, protecting users and organisations, meeting legal obligations, ensuring platform security, and preserving professional history.

## 3. Retention Principles
EthicCred may retain information only for legitimate business, operational, security, legal, verification, fraud-prevention, compliance, historical, and platform-integrity purposes. Retention periods vary by data type, legal requirements, user requests, platform needs, security considerations, and dispute status.

## 4. Account Information
**Active accounts**: retained while account remains active. **Closed accounts**: may be retained for up to 10 years following account closure for fraud prevention, security, audit, compliance, legal, and verification purposes.

## 5. Professional Passport Records
Employment, internship, volunteer, educational, training, achievement, recommendation, conduct, and experience records may be retained indefinitely unless removal is legally required, records are proven fraudulent, or applicable laws require deletion. Long-term retention preserves professional history and verification integrity.

## 6. Certificates
Certificates may be retained indefinitely to support long-term verification, fraud prevention, and historical authenticity. Revoked certificates may continue to be retained with revocation status.

## 7. Audit Logs
Profile access, verification activity, certificate issuance, updates, administrative actions, and security events — minimum 10 years, longer where required for security, fraud prevention, legal obligations, investigations, or platform integrity.

## 8. Verification Logs
Verification requests, results, viewer information, timestamps, and consent records — minimum 10 years. EthicCred may retain longer where necessary to protect platform integrity or comply with law.

## 9. Consent Records
Verification consent, sharing permissions, certificate acceptance, and data processing consent — minimum 10 years after consent withdrawal or account closure.

## 10. Security Logs
Login history, device information, IP addresses, security alerts, and authentication events — minimum 5 years. Longer retention may apply for fraud prevention, investigations, security incidents, or legal compliance.

## 11. Dispute Records
Complaints, evidence, investigations, decisions, and communications — minimum 10 years after dispute closure.

## 12. Fraud and Enforcement Records
Fraud investigations, account abuse, certificate fraud, impersonation incidents, and enforcement actions may be retained indefinitely where necessary to protect platform integrity, prevent repeat abuse, and maintain security.

## 13. Backups
EthicCred may maintain encrypted backup copies. Deleted information may remain within backup systems until backup rotation cycles complete.

## 14. Account Deletion Requests
Subject to legal obligations, audit requirements, verification integrity, active disputes, fraud investigations, and compliance. EthicCred may anonymise, restrict, archive, or retain certain records.

## 15. Record Archiving
EthicCred may archive inactive records for operational efficiency. Archived records remain available for verification, security, compliance, fraud prevention, and historical reference.

## 16. Legal Holds
EthicCred may suspend deletion where records are subject to litigation, government requests, regulatory inquiries, court orders, or internal/security investigations.

## 17. International Data Storage
Information may be stored, backed up, processed, or archived in multiple jurisdictions where EthicCred or its service providers operate.

## 18. Deletion Methods
Where deletion is appropriate, EthicCred may delete, anonymise, de-identify, restrict access, archive, or remove public visibility.

## 19. No Guarantee of Immediate Deletion
Deletion may not be immediate due to technical processes, backup systems, security, legal obligations, or fraud-prevention requirements.

## 20. Platform Integrity Exception
Where necessary to maintain verification integrity, auditability, fraud prevention, certificate authenticity, platform trust, and historical record validation, EthicCred may retain limited historical records even after account closure, subject to applicable law.

## 21. Policy Changes
EthicCred may update this Policy periodically. Continued use constitutes acceptance.

## 22. Contact
Questions regarding data retention, deletion, archiving, privacy, compliance, or record management may be directed to EthicCred through official legal, privacy, or support contact channels.`,
  },

  "trust-safety-policy": {
    slug: "trust-safety-policy",
    title: "Trust & Safety Enforcement Policy",
    category: "Trust & Safety",
    lastUpdated: LAST,
    placeholder: true,
    content: `## ETHICCRED TRUST & SAFETY ENFORCEMENT POLICY

## 1. Purpose
This Policy describes how EthicCred enforces its Terms of Service, Acceptable Use Policy, Community Guidelines, and other platform policies.

## 2. Detection
Violations may be detected through automated systems (fraud detection, duplicate detection, anomaly detection), user reports, dispute submissions, internal audits, and lawful third-party reports.

## 3. Review
Reports are reviewed by trained personnel. Decisions consider available evidence, context, the user's account history, the severity of the alleged violation, and applicable platform policies.

## 4. Enforcement Actions
Possible actions include warnings, requirements to provide additional verification, record suspension, certificate revocation, restriction of issuance privileges, account suspension, account termination, IP-level restrictions, and cooperation with lawful authorities.

## 5. Repeat Violations
Repeated violations or serious single violations may result in immediate account termination and permanent removal from the EthicCred network.

## 6. Appeals
Users may appeal enforcement decisions by submitting new evidence or context through the dispute system. EthicCred is not obligated to reopen decisions in the absence of new material.

## 7. Cooperation with Authorities
Where required by law or lawful order, EthicCred may cooperate with law enforcement, regulators, and other authorised parties.

## 8. Transparency
EthicCred may publish periodic transparency reports describing aggregate enforcement actions.`,
  },

  "api-terms": {
    slug: "api-terms",
    title: "API Terms of Use",
    category: "Developers",
    lastUpdated: LAST,
    placeholder: true,
    content: `## ETHICCRED API TERMS OF USE

## 1. Scope
These API Terms govern access to and use of EthicCred APIs, SDKs, webhook services, and developer tools.

## 2. Access
API access requires an approved developer account, valid API credentials, and acceptance of these terms in addition to the Platform Terms of Service and Privacy Policy.

## 3. Permitted Uses
APIs may be used to integrate verified credential reading, automated verifications, and bulk certificate issuance (where authorised). Use must be consistent with the user consents recorded on the Platform.

## 4. Rate Limits
API usage is subject to rate limits described in the developer documentation. EthicCred may throttle or temporarily suspend access that exceeds limits.

## 5. Webhooks
Webhook events must be processed securely. Failed deliveries may be retried; persistent failures may result in webhook subscriptions being disabled.

## 6. Security
Developers must store API keys securely, rotate them regularly, restrict their use to the smallest set of necessary IPs, and never expose keys in client-side code.

## 7. Data Handling
Data accessed through the API must be handled in accordance with the Platform Privacy Policy and Data Retention Policy. Data must not be cached longer than necessary and must not be redistributed to unauthorised third parties.

## 8. Service Levels
EthicCred uses commercially reasonable efforts to maintain API availability. Specific SLAs may apply to Enterprise and API plans.

## 9. Termination
EthicCred may suspend or terminate API access for violation of these terms, security risk, or non-payment.

## 10. Changes
EthicCred may modify API surfaces with reasonable advance notice. Breaking changes will normally be announced at least 90 days in advance.`,
  },

  "individual-consent": {
    slug: "individual-consent",
    title: "Individual Professional Passport Consent Agreement",
    category: "Registration",
    lastUpdated: LAST,
    placeholder: true,
    content: `## INDIVIDUAL PROFESSIONAL PASSPORT CONSENT AGREEMENT

By creating an EthicCred account as an individual professional, you consent to the following:

## 1. Creation of Passport
EthicCred will create a Global EthicCred ID for you and a Professional Passport that will hold your verified records over time.

## 2. Receipt of Records
Authorised organisations (employers, institutions, training providers) may issue verified certificates, recommendations, and conduct records directly to your passport.

## 3. Storage and Processing
You authorise EthicCred to store, process, verify, and display authorised records in accordance with the Privacy Policy and Consent & Verification Policy.

## 4. Verification
You consent to verification requests being made against your passport by employers, recruiters, institutions, and other parties acting within their permitted access rights.

## 5. Visibility
You control the visibility of your passport between Private, Verification-Only (default), Shared-Link, and Public. You may change visibility at any time from your privacy settings.

## 6. Audit Logging
Verification requests, profile views, certificate issuance events, and consent changes are recorded in your audit log.

## 7. Withdrawal
You may withdraw consent for processing where legally permitted. Some records previously verified and relied upon by third parties may continue to exist in audit logs to preserve verification integrity.

## 8. Truthfulness
You confirm that the information you submit yourself is accurate to the best of your knowledge.

## 9. Acknowledgement
EthicCred is not the issuer of certificates issued by third parties and does not certify your character, future conduct, or fitness for any particular role.`,
  },

  "employer-agreement": {
    slug: "employer-agreement",
    title: "Employer Authorization & Issuer Agreement",
    category: "Registration",
    lastUpdated: LAST,
    placeholder: true,
    content: `## EMPLOYER AUTHORIZATION & ISSUER AGREEMENT

By creating an EthicCred Organisation account, the registering individual represents and warrants to EthicCred that:

## 1. Authority
They are authorised by the organisation to bind it to this Agreement and to issue certificates and verifications on its behalf.

## 2. Issuance Responsibility
The organisation will only issue records that are accurate, lawful, authorised, professionally appropriate, and supported by internal evidence. The organisation accepts sole responsibility for the content of records it issues.

## 3. Conduct Records
Conduct certificates and recommendations will be based on professional workplace observations and will not contain criminal accusations, defamatory content, medical information, or unauthorised personal data.

## 4. Revocation
The organisation will promptly revoke records where errors are discovered, fraud is suspected, or authorisation is withdrawn.

## 5. Authorised Representatives
The organisation will manage which of its employees may issue records. The organisation remains responsible for the actions of its authorised representatives.

## 6. Compliance
The organisation will comply with the Certificate Issuance Policy, Consent & Verification Policy, Privacy Policy, Trust & Safety Enforcement Policy, and Dispute Resolution Policy.

## 7. Audit
The organisation acknowledges that EthicCred may retain audit logs of issuance events and may suspend issuance privileges where serious or repeated violations occur.

## 8. Termination
EthicCred may suspend or terminate organisation accounts that violate platform policies. Certificates already issued may continue to be verifiable for audit purposes subject to the Certificate Issuance Policy.`,
  },

  "institution-agreement": {
    slug: "institution-agreement",
    title: "Institution Authorization & Issuer Agreement",
    category: "Registration",
    lastUpdated: LAST,
    placeholder: true,
    content: `## INSTITUTION AUTHORIZATION & ISSUER AGREEMENT

By creating an EthicCred Institution account, the registering representative confirms to EthicCred that:

## 1. Authority
They are authorised by the institution (university, school, training body, or similar) to bind it to this Agreement and to issue credentials on its behalf.

## 2. Credential Authority
The institution holds all legal, accreditation, and regulatory authority required to issue the credentials it places on the Platform.

## 3. Student & Alumni Records
Records issued to students, interns, and alumni will be accurate, complete, and supported by institutional records.

## 4. Conduct Certificates
Where conduct certificates are issued, they will reflect institutional observations and assessments and will not contain criminal allegations, medical data, or unauthorised personal information.

## 5. Bulk Issuance
The institution accepts responsibility for the accuracy of records issued in bulk and will reconcile records against authoritative institutional sources.

## 6. Revocation
The institution will revoke records where issued in error, on plagiarism findings, or where supporting records are subsequently invalidated.

## 7. Compliance
The institution will comply with the Certificate Issuance Policy, Consent & Verification Policy, Privacy Policy, Dispute Resolution Policy, and applicable educational regulations.

## 8. Verification
The institution authorises EthicCred to operate verification services that confirm whether a credential was issued by the institution and remains valid.

## 9. Termination
EthicCred may suspend or terminate institution accounts that violate platform policies.`,
  },

  "recruiter-agreement": {
    slug: "recruiter-agreement",
    title: "Recruiter Verification Usage Agreement",
    category: "Registration",
    lastUpdated: LAST,
    placeholder: true,
    content: `## RECRUITER VERIFICATION USAGE AGREEMENT

By creating an EthicCred Recruiter account, you (the recruiter or recruiting firm) agree to:

## 1. Lawful Use
Use EthicCred verifications only for lawful recruitment, screening, employer-of-record, and background-context purposes.

## 2. Consent Awareness
Acknowledge that verification represents data available to the Platform at the time of verification and does not constitute legal clearance, character certification, or guarantee of employment suitability.

## 3. Confidentiality
Treat data accessed through verification services as confidential, share it only with authorised parties involved in the hiring decision, and not redistribute, publish, or sell verification data.

## 4. Anti-Discrimination
Apply EthicCred data in compliance with applicable equal-opportunity and anti-discrimination laws.

## 5. Candidate Communication
Communicate honestly and promptly with candidates regarding verification outcomes where required by law or by EthicCred policy.

## 6. Audit Cooperation
Acknowledge that verification requests are logged and may be reviewed if abuse, mass-scraping, or impermissible use is suspected.

## 7. Prohibited Uses
Do not use the Platform to harass candidates, to penalise candidates for raising disputes, or to retaliate against legitimate complaints.

## 8. Termination
EthicCred may suspend or terminate recruiter access for violation of this Agreement or for security, fraud, or compliance reasons.`,
  },

  "contact": {
    slug: "contact",
    title: "Contact Legal Team",
    category: "Core",
    lastUpdated: LAST,
    placeholder: true,
    content: `## CONTACT THE ETHICCRED LEGAL TEAM

For legal, privacy, compliance, copyright, or trust & safety matters, please reach the appropriate team below.

## General Legal
**legal@ethiccred.com**

## Privacy & Data Protection
**privacy@ethiccred.com**

## Trust & Safety
**trust@ethiccred.com**

## Disputes
File a dispute directly from your dashboard, or write to **disputes@ethiccred.com**.

## Copyright (DMCA)
**copyright@ethiccred.com**

## Postal Address
EthicCred, Attn: Legal — [Address will be updated when the company finalises its registered office].

We endeavour to respond to legitimate legal inquiries within 14 business days. Inquiries that are clearly abusive, repetitive, or harassing may not receive a response.`,
  },
};

// Footer navigation order — matches the spec
export const LEGAL_FOOTER_ORDER = [
  "terms-of-service",
  "privacy-policy",
  "cookie-policy",
  "acceptable-use-policy",
  "community-guidelines",
  "intellectual-property-policy",
  "subscription-refund-policy",
  "data-retention-policy",
  "trust-safety-policy",
  "contact",
];

export const SIDEBAR_GROUPS = [
  { label: "Core", slugs: ["terms-of-service", "privacy-policy", "consent-verification-policy", "contact"] },
  { label: "Privacy", slugs: ["cookie-policy", "data-retention-policy"] },
  { label: "Issuers", slugs: ["certificate-issuance-policy", "employer-agreement", "institution-agreement", "recruiter-agreement", "individual-consent"] },
  { label: "Trust & Safety", slugs: ["acceptable-use-policy", "community-guidelines", "trust-safety-policy", "dispute-resolution-policy", "intellectual-property-policy"] },
  { label: "Billing", slugs: ["subscription-refund-policy"] },
  { label: "Developers", slugs: ["api-terms"] },
];
